import React, { useState } from 'react';
import { notify } from '../../utils/toast';
// import { useAuth } from '../../hooks/chat/useAuth';
import { getAuthData } from "../../contexts/AuthContext";
import { getAiApiUrl } from '../../config';

interface UnshareDialogProps {
  conversationId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const UnshareDialog: React.FC<UnshareDialogProps> = ({
  conversationId,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  
  // Use a ref to track if we've already made the API call
  const apiCallMadeRef = React.useRef(false);

  React.useEffect(() => {
    // Only make the API call once
    if (apiCallMadeRef.current) return;
    apiCallMadeRef.current = true;

    const unshareConversation = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const auth = await getAuthData();
        const response = await fetch(`${getAiApiUrl()}/unshare-convo`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth.jwt}`
          },
          body: JSON.stringify({
            walletAddress: auth.walletAddress,
            nonce: auth.nonce,
            conversation_id: conversationId,
            user: auth.did.toLowerCase().replaceAll('_uuid', ':uuid').replaceAll('_addr', ':addr').replaceAll('_asa', ':asa').replaceAll('_app', ':app').replaceAll('_txn', ':txn')
          })
        });

        if (!response.ok) {
          throw new Error('Failed to unshare conversation');
        }

        // Call the onSuccess callback to update the UI
        await onSuccess();
        notify.success('Conversation successfully unshared');
        
        // Close the dialog after a short delay
        setTimeout(() => {
          onClose();
        }, 1500);
      } catch (error) {
        console.error('Error unsharing conversation:', error);
        setError('Failed to unshare conversation. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    unshareConversation();
  }, [conversationId, onClose, onSuccess]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6 text-gray-900 dark:text-white">
        <h2 className="text-xl font-bold mb-4">Unshare Conversation</h2>
        
        {loading && (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            <p className="mt-4">Removing shared access...</p>
          </div>
        )}
        
        {error && (
          <div className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 rounded-lg mb-4">
            {error}
          </div>
        )}
        
        {!loading && !error && (
          <div className="flex flex-col items-center py-8">
            <div className="text-green-500 dark:text-green-400 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <p className="text-center">Conversation successfully unshared</p>
          </div>
        )}
        
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg transition-colors"
            disabled={loading}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
